import { routes, URL } from 'appConstants';
import type { Resolution } from 'types';
import { _t_back } from 'utils';

import linkNews from 'assets/img/resolutions-v4/see-news.png';
import linkEvents from 'assets/img/resolutions-v4/see-events.png';
import linkResolutions from 'assets/img/resolutions-v4/see-resolutions.png';
import linkAnalytics from 'assets/img/resolutions-v4/see-analytics.png';

export type LibraryFilterKey =
  | 'scope_product_export'
  | 'support_measures'
  | 'filter_application_deadline'
  | 'size_of_enterprise'
  | 'grants_and_loands';

export type LibraryFilterState = Record<LibraryFilterKey, number[]>;

export type LibraryFilterDef = {
  key: LibraryFilterKey,
  label: string,
  url: string,
  mode: 'multi' | 'single',
};

export const LIBRARY_FILTER_DEFS: LibraryFilterDef[] = [
  {
    key: 'scope_product_export',
    label: 'Область продукции',
    url: URL.RESOLUTIONS.FILTERS.SCOPE_OF_PRODUCTS,
    mode: 'multi',
  },
  {
    key: 'support_measures',
    label: 'Направление мер поддержки',
    url: URL.RESOLUTIONS.FILTERS.SUPPORT_MEASURES,
    mode: 'multi',
  },
  {
    key: 'filter_application_deadline',
    label: 'Сроки подачи',
    url: URL.RESOLUTIONS.FILTERS.DATA_CHOICE,
    mode: 'single',
  },
  {
    key: 'size_of_enterprise',
    label: 'Размер предприятия',
    url: URL.RESOLUTIONS.FILTERS.SIZE_OF_ENTERPRISE,
    mode: 'single',
  },
  {
    key: 'grants_and_loands',
    label: 'Формат предоставления меры поддержки',
    url: URL.RESOLUTIONS.FILTERS.GRAND_AND_LOANS,
    mode: 'multi',
  },
];

export const LIBRARY_FILTERS = LIBRARY_FILTER_DEFS.map((item) => item.label);

export const emptyLibraryFilters = (): LibraryFilterState => ({
  scope_product_export: [],
  support_measures: [],
  filter_application_deadline: [],
  size_of_enterprise: [],
  grants_and_loands: [],
});

export const hasActiveLibraryFilters = (filters: LibraryFilterState): boolean => (
  LIBRARY_FILTER_DEFS.some((def) => filters[def.key].length > 0)
);

export const SEE_ALSO = [
  {
    id: 'news',
    title: 'Новости',
    to: routes.media.news.root,
    image: linkNews,
  },
  {
    id: 'events',
    title: 'Мероприятия',
    to: routes.events.root,
    image: linkEvents,
  },
  {
    id: 'resolutions',
    title: 'Меры поддержки',
    to: routes.resolutions.root,
    image: linkResolutions,
  },
  {
    id: 'analytics',
    title: 'Аналитика',
    to: `${routes.resolutions.service.root}/analytics`,
    image: linkAnalytics,
  },
];

export const getSeeAlso = (excludeIds: string[] = []) => (
  SEE_ALSO.filter((item) => !excludeIds.includes(item.id))
);

export const MOSPROM_ORG = 'Автономная некоммерческая организация \n«Центр поддержки экспорта, промышленности\nи инвестиционной деятельности «МОСПРОМ»';

export const getResolutionRegion = (item: Resolution): string => {
  const title = item.moscow_or_rf?.short_description ||
    item.moscow_or_rf?.title ||
    'МОСКВА';
  return String(title).toUpperCase();
};

export const getResolutionOrg = (item: Resolution): string => {
  const org = item.organizer_of_resolution?.[0];
  if (org?.title) {
    return org.title;
  }
  if (org?.short_title) {
    return org.short_title;
  }
  return MOSPROM_ORG;
};

const getEntityId = (value?: { id?: number } | null): number | null => (
  value && typeof value.id === 'number' ? value.id : null
);

export const matchLibraryFilters = (
  item: Resolution,
  filters: LibraryFilterState,
): boolean => {
  const scopeSelected = filters.scope_product_export;
  if (scopeSelected.length) {
    const id = getEntityId(item.scope_product_export);
    if (id == null || !scopeSelected.includes(id)) {
      return false;
    }
  }

  const supportSelected = filters.support_measures;
  if (supportSelected.length) {
    const ids = (item.support_measures || [])
      .map((entry) => getEntityId(entry))
      .filter((id): id is number => id != null);
    if (!supportSelected.some((id) => ids.includes(id))) {
      return false;
    }
  }

  const deadlineSelected = filters.filter_application_deadline;
  if (deadlineSelected.length) {
    const id = getEntityId(item.filter_application_deadline);
    if (id == null || !deadlineSelected.includes(id)) {
      return false;
    }
  }

  const sizeSelected = filters.size_of_enterprise;
  if (sizeSelected.length) {
    const id = getEntityId(item.size_of_enterprise);
    if (id == null || !sizeSelected.includes(id)) {
      return false;
    }
  }

  const grantSelected = filters.grants_and_loands;
  if (grantSelected.length) {
    const id = getEntityId(item.grants_and_loands);
    if (id == null || !grantSelected.includes(id)) {
      return false;
    }
  }

  return true;
};

export const matchResolutionSearch = (item: Resolution, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  const haystack = [
    _t_back(item, 'res_title'),
    item.res_title,
    getResolutionOrg(item),
    getResolutionRegion(item),
  ].join(' ').toLowerCase();
  return haystack.includes(needle);
};
